/**
 * Type IIP ライブラリ（§5.5 I62–I63）。
 * 認識は IUPAC。`cutAfter` は + 鎖先頭からの 0-based 切断位置。
 * `overhang` >0 は 5′、=0 は blunt、<0 は 3′。
 */

export type OverhangKind = "5'" | "3'" | 'blunt';

export interface RestrictionEnzyme {
  id: string;
  name: string;
  pattern: string;
  cutAfter: number;
  overhang: number;
  starter?: boolean;
}

function enzyme(
  name: string,
  pattern: string,
  cutAfter: number,
  overhang: number,
  starter = false
): RestrictionEnzyme {
  return { id: name.toLowerCase(), name, pattern, cutAfter, overhang, starter };
}

export function overhangKind(enzyme: RestrictionEnzyme): OverhangKind {
  if (enzyme.overhang > 0) return "5'";
  if (enzyme.overhang < 0) return "3'";
  return 'blunt';
}

/** スターター 25（I63）＋常用 Type IIP */
export const RESTRICTION_LIBRARY: RestrictionEnzyme[] = [
  enzyme('EcoRI', 'GAATTC', 1, 4, true),
  enzyme('BamHI', 'GGATCC', 1, 4, true),
  enzyme('HindIII', 'AAGCTT', 1, 4, true),
  enzyme('PstI', 'CTGCAG', 5, -4, true),
  enzyme('SalI', 'GTCGAC', 1, 4, true),
  enzyme('SmaI', 'CCCGGG', 3, 0, true),
  enzyme('XbaI', 'TCTAGA', 1, 4, true),
  enzyme('XhoI', 'CTCGAG', 1, 4, true),
  enzyme('XmaI', 'CCCGGG', 1, 4, true),
  enzyme('KpnI', 'GGTACC', 5, -4, true),
  enzyme('SacI', 'GAGCTC', 5, -4, true),
  enzyme('SacII', 'CCGCGG', 4, -2, true),
  enzyme('SphI', 'GCATGC', 5, -4, true),
  enzyme('NcoI', 'CCATGG', 1, 4, true),
  enzyme('NdeI', 'CATATG', 2, 2, true),
  enzyme('NheI', 'GCTAGC', 1, 4, true),
  enzyme('NotI', 'GCGGCCGC', 2, 4, true),
  enzyme('ApaI', 'GGGCCC', 5, -4, true),
  enzyme('AgeI', 'ACCGGT', 1, 4, true),
  enzyme('AvrII', 'CCTAGG', 1, 4, true),
  enzyme('BglII', 'AGATCT', 1, 4, true),
  enzyme('ClaI', 'ATCGAT', 2, 2, true),
  enzyme('EcoRV', 'GATATC', 3, 0, true),
  enzyme('HpaI', 'GTTAAC', 3, 0, true),
  enzyme('PvuI', 'CGATCG', 4, -2, true),
  enzyme('PvuII', 'CAGCTG', 3, 0),
  enzyme('ScaI', 'AGTACT', 3, 0),
  enzyme('SpeI', 'ACTAGT', 1, 4),
  enzyme('StuI', 'AGGCCT', 3, 0),
  enzyme('AatII', 'GACGTC', 5, -4),
  enzyme('AccI', 'GTMKAC', 2, 2),
  enzyme('AflII', 'CTTAAG', 1, 4),
  enzyme('AflIII', 'ACRYGT', 1, 4),
  enzyme('AluI', 'AGCT', 2, 0),
  enzyme('ApaLI', 'GTGCAC', 1, 4),
  enzyme('AscI', 'GGCGCGCC', 2, 4),
  enzyme('AseI', 'ATTAAT', 2, 2),
  enzyme('AvaI', 'CYCGRG', 1, 4),
  enzyme('BclI', 'TGATCA', 1, 4),
  enzyme('BsaHI', 'GRCGYC', 2, 2),
  enzyme('BsiWI', 'CGTACG', 1, 4),
  enzyme('BspEI', 'TCCGGA', 1, 4),
  enzyme('BspHI', 'TCATGA', 1, 4),
  enzyme('BsrGI', 'TGTACA', 1, 4),
  enzyme('BssHII', 'GCGCGC', 1, 4),
  enzyme('BstBI', 'TTCGAA', 2, 2),
  enzyme('BstEII', 'GGTNACC', 1, 5),
  enzyme('BstXI', 'CCANNNNNNTGG', 8, -4),
  enzyme('BstZ17I', 'GTATAC', 3, 0),
  enzyme('DraI', 'TTTAAA', 3, 0),
  enzyme('EagI', 'CGGCCG', 1, 4),
  enzyme('EcoNI', 'CCTNNNNNAGG', 5, 1),
  enzyme('FseI', 'GGCCGGCC', 6, -4),
  enzyme('FspI', 'TGCGCA', 3, 0),
  enzyme('HaeII', 'RGCGCY', 5, -4),
  enzyme('HaeIII', 'GGCC', 2, 0),
  enzyme('HhaI', 'GCGC', 3, -2),
  enzyme('HincII', 'GTYRAC', 3, 0),
  enzyme('HinfI', 'GANTC', 1, 3),
  enzyme('KasI', 'GGCGCC', 1, 4),
  enzyme('MfeI', 'CAATTG', 1, 4),
  enzyme('MluI', 'ACGCGT', 1, 4),
  enzyme('MscI', 'TGGCCA', 3, 0),
  enzyme('MspI', 'CCGG', 1, 2),
  enzyme('NaeI', 'GCCGGC', 3, 0),
  enzyme('NarI', 'GGCGCC', 2, 2),
  enzyme('NruI', 'TCGCGA', 3, 0),
  enzyme('NsiI', 'ATGCAT', 5, -4),
  enzyme('PacI', 'TTAATTAA', 5, -2),
  enzyme('PciI', 'ACATGT', 1, 4),
  enzyme('PmeI', 'GTTTAAAC', 4, 0),
  enzyme('PmlI', 'CACGTG', 3, 0),
  enzyme('PspOMI', 'GGGCCC', 1, 4),
  enzyme('PspXI', 'VCTCGAGB', 2, 4),
  enzyme('RsaI', 'GTAC', 2, 0),
  enzyme('SbfI', 'CCTGCAGG', 6, -4),
  enzyme('SexAI', 'ACCWGGT', 1, 5),
  enzyme('SfcI', 'CTRYAG', 1, 4),
  enzyme('SfoI', 'GGCGCC', 3, 0),
  enzyme('SgrAI', 'CRCCGGYG', 2, 4),
  enzyme('SnaBI', 'TACGTA', 3, 0),
  enzyme('SspI', 'AATATT', 3, 0),
  enzyme('SwaI', 'ATTTAAAT', 4, 0),
  enzyme('TaqI', 'TCGA', 1, 2),
  enzyme('TliI', 'CTCGAG', 1, 4),
  enzyme('Tth111I', 'GACNNNGTC', 4, 1),
  enzyme('XcmI', 'CCANNNNNNNNNTGG', 8, -1),
  enzyme('XmnI', 'GAANNNNTTC', 5, 0),
  enzyme('ZraI', 'GACGTC', 3, 0),
  enzyme('AfeI', 'AGCGCT', 3, 0),
  enzyme('AhdI', 'GACNNNNNGTC', 6, -1),
  enzyme('AleI', 'CACNNNNGTG', 5, 0),
  enzyme('BanI', 'GGYRCC', 1, 4),
  enzyme('BanII', 'GRGCYC', 5, -4),
  enzyme('BsaAI', 'YACGTR', 3, 0),
  enzyme('BsaBI', 'GATNNNNATC', 5, 0),
  enzyme('BsaWI', 'WCCGGW', 1, 4),
  enzyme('BsrFI', 'RCCGGY', 1, 4),
  enzyme('StyI', 'CCWWGG', 1, 4),
  enzyme('BstYI', 'RGATCY', 1, 4),
  enzyme('DdeI', 'CTNAG', 1, 3),
  enzyme('Sau3AI', 'GATC', 0, 4),
  enzyme('NlaIII', 'CATG', 4, -4),
  enzyme('MboI', 'GATC', 0, 4),
  enzyme('EcoO109I', 'RGGNCCY', 2, 3),
  enzyme('Bsp1286I', 'GDGCHC', 5, -3),
  enzyme('Hin1I', 'GRCGYC', 2, 2),
  enzyme('Psp1406I', 'AACGTT', 2, 2),
];

export const STARTER_ENZYME_IDS = RESTRICTION_LIBRARY.filter((e) => e.starter).map((e) => e.id);

export function findEnzyme(
  idOrName: string,
  extras: RestrictionEnzyme[] = []
): RestrictionEnzyme | undefined {
  const key = idOrName.toLowerCase();
  return (
    extras.find((e) => e.id === key || e.name.toLowerCase() === key) ??
    RESTRICTION_LIBRARY.find((e) => e.id === key || e.name.toLowerCase() === key)
  );
}

export function customEnzyme(
  name: string,
  pattern: string,
  cutAfter: number,
  overhang: number
): RestrictionEnzyme | null {
  const trimmed = name.trim();
  if (!trimmed || !/^[ACGTURYSWKMBDHVN]+$/i.test(pattern) || pattern.length < 4) return null;
  return {
    id: `custom:${trimmed.toLowerCase()}`,
    name: trimmed,
    pattern: pattern.toUpperCase(),
    cutAfter,
    overhang,
  };
}
